import './styles.css';
import { usePathLookupIpAutocompleteMulticast } from 'components/Graph/GraphSidePanel/GraphPathlookup/usePathLookupIpAutocompleteMulticast';

import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import { Button } from 'components/Button/Button';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { FormikSwitchField } from 'components/FormikSwitchField/FormikSwitchField';
import { FormikTypeaheadInput } from 'components/FormikTypeaheadInput/FormikTypeaheadInput';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import type { MulticastFormValues } from 'components/Graph/GraphSidePanel/GraphPathlookup/MulticastFormValues';
import { useLocale } from 'containers/App/LocaleContext';
import type { FormikProps } from 'formik';
import { type FC, useEffect, useRef } from 'react';
import type { SyntheticEvent } from 'react';
import { getBooleanOptionValue } from 'utils/form/getBooleanOptionValue';
import { isValidIpv4Address } from 'utils/formValidation/validations';
import { scrollErrorIntoView } from 'utils/formik/scrollErrorIntoView';
import { InfoIcon } from '../icons/InfoIcon';
import { GraphFirstHopEntryPoints } from './GraphFirstHopEntryPoints';
import { PathLookupAdvancedOptions } from './PathLookupAdvancedOptions';
import { PathLookupDropdownMenu } from './PathLookupDropdownMenu';
import type { Vrf } from './fetchVrf';
import { getVrfOption } from './helpers/getVrfOption';
import { isMulticastAddress } from './helpers/isMulticastAddress';

interface Props {
  resetAdvancedOptionsToSelectedPreset: () => void;
  formik: FormikProps<MulticastFormValues>;
  onProtocolChange: (value: string | null) => void;
  vrf: Vrf[];
}

const SELECT_WIDTH = 130;

export const GraphMulticastForm: FC<Props> = (props) => {
  const msg = useLocale();
  const formRef = useRef<HTMLFormElement>(null);

  const ctxProps = useGraphContextSelectors({
    graphLoading: (s) => s.loading === 'LOADING',
  });

  const sourceAutocomplete = usePathLookupIpAutocompleteMulticast({
    excludeIps: [props.formik.values.group, props.formik.values.receiver],
    type: 'source',
  });

  const groupAutocomplete = usePathLookupIpAutocompleteMulticast({
    excludeIps: [props.formik.values.source, props.formik.values.receiver],
    type: 'group',
  });

  const receiverAutocomplete = usePathLookupIpAutocompleteMulticast({
    excludeIps: [props.formik.values.source, props.formik.values.group],
    type: 'receiver',
  });

  const vrfOptions = props.vrf.map((item) => getVrfOption(item, msg));

  const handleStartButtonClick = (e: SyntheticEvent) => {
    e.preventDefault();

    props.formik.submitForm();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger on submit
  useEffect(() => {
    // delay to next tic so advanced options can be uncollapsed if needed
    setTimeout(() => {
      if (!props.formik.isValid) {
        scrollErrorIntoView(formRef);
      }
    }, 0);
  }, [props.formik.submitCount]);

  return (
    <form className='relative my-4' ref={formRef}>
      <div className='mx-5'>
        <FormikSwitchField
          name='firstHopAlgorithm'
          label={
            msg.canvasGraph.graphSidePanel.pathlookup.multicast
              .firstHopAlgorithm
          }
          formik={props.formik}
          options={[
            {
              label:
                msg.canvasGraph.graphSidePanel.pathlookup.multicast.automatic,
              value: 'automatic',
            },
            {
              label:
                msg.canvasGraph.graphSidePanel.pathlookup.multicast.userDefined,
              value: 'userDefined',
            },
          ]}
          size='small'
        />
        {props.formik.values.firstHopAlgorithm === 'automatic' && (
          <FormikSelectField
            label={msg.canvasGraph.graphSidePanel.pathlookup.unicast.vrf}
            formik={props.formik}
            name='vrf'
            options={vrfOptions}
            nonClearable
          />
        )}
        {props.formik.values.firstHopAlgorithm === 'userDefined' && (
          <GraphFirstHopEntryPoints formik={props.formik} />
        )}
        <div className='relative'>
          <FormikTypeaheadInput
            label={msg.canvasGraph.graphSidePanel.pathlookup.multicast.source}
            autoTrim='all'
            formik={props.formik}
            inputClassName='ipf-input'
            loading={sourceAutocomplete.loading}
            menuContent={
              <PathLookupDropdownMenu
                data={sourceAutocomplete.content}
                name='source'
                formik={props.formik}
              />
            }
            name='source'
            onFocus={sourceAutocomplete.search}
            onSearch={sourceAutocomplete.search}
          />
        </div>
        <div className='relative flex'>
          <div className='flex-1'>
            <FormikTypeaheadInput
              label={msg.canvasGraph.graphSidePanel.pathlookup.multicast.group}
              autoTrim='all'
              formik={props.formik}
              inputClassName='ipf-input'
              loading={groupAutocomplete.loading}
              menuContent={
                <PathLookupDropdownMenu
                  data={groupAutocomplete.content}
                  name='group'
                  formik={props.formik}
                  withDns={false}
                />
              }
              name='group'
              onFocus={groupAutocomplete.search}
              onSearch={groupAutocomplete.search}
            />
          </div>
          <div className='relative ml-2.5' style={{ width: SELECT_WIDTH }}>
            <FormikTypeaheadInput
              label={
                msg.canvasGraph.graphSidePanel.pathlookup.multicast.receiver
              }
              autoTrim='all'
              formik={props.formik}
              inputClassName='ipf-input'
              loading={receiverAutocomplete.loading}
              menuContent={
                <PathLookupDropdownMenu
                  data={receiverAutocomplete.content}
                  name='receiver'
                  formik={props.formik}
                />
              }
              name='receiver'
              onFocus={receiverAutocomplete.search}
              onSearch={receiverAutocomplete.search}
              wrapperClassName='relative'
            />
          </div>
        </div>
        {
          // We cannot use formik validation because fields are validated only on touch/submit so it's initially valid
          isValidIpv4Address(props.formik.values.group) &&
            !isMulticastAddress(props.formik.values.group) && (
              <div className='mt-2.5 flex items-center'>
                <div className='mr-2.5'>
                  <InfoIcon title={null} width='16' height='16' />
                </div>
                <div>
                  {
                    msg.canvasGraph.graphSidePanel.pathlookup.multicast
                      .unicastGroupWarning
                  }
                </div>
              </div>
            )
        }
      </div>
      <PathLookupAdvancedOptions
        resetAdvancedOptionsToSelectedPreset={
          props.resetAdvancedOptionsToSelectedPreset
        }
        formik={props.formik}
        onProtocolChange={props.onProtocolChange}
      />
      <div className='px-5 pt-4'>
        <FormikSwitchField
          name='securedPath'
          label={
            <>
              {msg.canvasGraph.graphSidePanel.pathlookup.multicast.securedPath}
              <TooltipIcon
                content={
                  msg.canvasGraph.graphSidePanel.pathlookup.multicast
                    .securedPathTooltip
                }
                iconProps={{
                  inline: true,
                  iconStyle: 'solid',
                }}
                size='sm'
                className='ml-2'
              />
            </>
          }
          formik={props.formik}
          options={[
            {
              label: msg.canvasGraph.graphSidePanel.pathlookup.unicast.drop,
              value: true,
            },
            {
              label: msg.canvasGraph.graphSidePanel.pathlookup.unicast.continue,
              value: false,
            },
          ]}
          getOptionValue={getBooleanOptionValue}
          size='small'
        />
      </div>
      <div className='mt-3 flex justify-center'>
        <Button
          variant='primary'
          onClick={handleStartButtonClick}
          size='sm'
          loading={ctxProps.graphLoading}
          type='submit'
        >
          {msg.canvasGraph.graphSidePanel.pathlookup.multicast.btnSubmit}
        </Button>
      </div>
    </form>
  );
};
